/**
 * The one place an amount becomes a string.
 *
 * WHY THIS LIVES IN `shared` AND NOT IN `@gladis/ui`
 *
 * It is a pure function, and `apps/api` may legitimately need it — an invite email quoting a
 * balance, a CSV export — while being forbidden from importing `@gladis/ui`, which is a React
 * package. `packages/shared` is the leaf every layer may import, so putting it here is what makes
 * "exactly one implementation" (SC-003) achievable rather than aspirational. In `ui` it would be
 * reachable by the browser and by nothing else, and the API would grow a second one.
 *
 * Two decimals, always. `docs/project-overview/data-model.md` §Monetary Precision fixes this for
 * every monetary value in the product — balances, expenses, stock prices, valuations, mortgage
 * principals and derived interest alike — so there is no per-context variation to parameterise.
 *
 * EUR only, which is a constitutional constraint (Principle VII) rather than a default. If a second
 * currency is ever wanted, that is a constitution amendment first and a signature change second.
 */

/**
 * The household's locale, and the answer for every consumer that never chooses one.
 *
 * It decides that a thousands separator is a dot and the decimal mark a comma — the difference
 * between €1.234,50 and €1,234.50. Getting that backwards is not cosmetic: it changes which number
 * a reader believes they are looking at.
 *
 * IT IS ALSO THE FALLBACK ON PURPOSE, and `apps/api` is why. A CSV export or an invite email quoting
 * a balance has no browser and no reader to ask, and picking `en-US` there would render the Dutch
 * household's own figures in a foreign shape. A server that never calls the setter keeps this.
 */
const DEFAULT_LOCALE = "nl-NL";

/**
 * THE FORMATTING LOCALE IS AMBIENT MODULE STATE, WHICH IS A DELIBERATE TRADE.
 *
 * `formatEur` is called from 41 files, most of them inside domain `/ui` packages, and several of
 * them from plain functions rather than components — a chart's tick formatter, a card's hint
 * builder. The two alternatives both cost more than they are worth here:
 *
 *   A SECOND PARAMETER means threading a locale through 41 call sites and every function that
 *   wraps one, and a caller who forgets it silently gets the household's format instead of the
 *   reader's — which looks right in the Netherlands and is wrong everywhere else.
 *
 *   A REACT CONTEXT cannot be read from the non-component callers at all, and would put a React
 *   dependency in front of a function `apps/api` is explicitly allowed to use.
 *
 * So the locale is resolved ONCE, before the first render, and read from here. What keeps that
 * honest is that it is write-once-at-boot in practice: `apps/app/src/main.tsx` is the only caller,
 * and nothing re-reads a formatted string after the fact.
 */
let activeLocale = DEFAULT_LOCALE;

/** One `Intl.NumberFormat` per locale. Constructing one is expensive; this is called per figure. */
const FORMATTERS = new Map<string, Intl.NumberFormat>();

function formatterFor(locale: string): Intl.NumberFormat {
  const cached = FORMATTERS.get(locale);
  if (cached !== undefined) return cached;

  const made = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  FORMATTERS.set(locale, made);
  return made;
}

/**
 * Chooses the locale every later `formatEur` renders in, and reports the one actually adopted.
 *
 * EUR IS STILL THE ONLY CURRENCY (Principle VII). This changes the SHAPE of the number — where the
 * grouping marks fall and which character is the decimal — and never the unit. A member in Boston
 * sees the household's euros written the way they read numbers, not dollars.
 *
 * IT CANNOT THROW, AND THAT IS THE POINT OF THE GUARD. `new Intl.NumberFormat` raises `RangeError`
 * on a malformed tag, and `navigator.language` is attacker-adjacent input in the sense that matters
 * here — it is whatever the browser was configured with. A throw would propagate into whichever
 * domain `/ui` package rendered the first figure, and those packages may not `try`/`catch` at all
 * (Principle V), so nothing they call may throw. A tag this runtime cannot honour leaves the
 * previous locale in place rather than taking the application down over a thousands separator.
 *
 * @returns the locale now in force — the requested one if it was usable, the previous one if not.
 */
export function setMoneyLocale(locale: string): string {
  try {
    // Constructing it IS the validation, and it warms the cache in the same step.
    formatterFor(locale);
    activeLocale = locale;
  } catch {
    // Deliberately silent about the tag and deliberately not a rethrow. See above.
  }
  return activeLocale;
}

/** The locale figures are currently rendered in. For tests and for anything that reports state. */
export function moneyLocale(): string {
  return activeLocale;
}

/**
 * One `Intl.NumberFormat` per locale, for shares rather than for money.
 *
 * A SECOND CACHE AND NOT A SECOND OPTIONS ARGUMENT ON `formatterFor`, because the currency formatter
 * above is warmed by `setMoneyLocale` as its validation step — a shared cache keyed on options would
 * make that warm-up warm one shape and validate for two.
 */
const PERCENT_FORMATTERS = new Map<string, Intl.NumberFormat>();

function percentFormatterFor(locale: string): Intl.NumberFormat {
  const cached = PERCENT_FORMATTERS.get(locale);
  if (cached !== undefined) return cached;

  const made = new Intl.NumberFormat(locale, {
    style: "percent",
    minimumFractionDigits: 0,
    /*
     * TWO, because that is the precision the domain stores. A share is held in basis points, so
     * `33.34%` is exact and a third decimal cannot exist — rendering more would invent one, and
     * rendering fewer would make three equal thirds read as `33%`, `33%`, `33%` beside a rule that
     * says they total exactly 100%.
     */
    maximumFractionDigits: 2,
  });
  PERCENT_FORMATTERS.set(locale, made);
  return made;
}

/**
 * A share, as `60%` or `33,34%`, in the reader's own number format.
 *
 * IT SHARES `formatEur`'s LOCALE AND NOTHING ELSE. A percentage is not money — no currency, no fixed
 * two decimals — but it is written with the same decimal mark, and a table showing `€1.234,50` in one
 * column and `33.34%` in the next has two number formats on one row. Reading the ambient locale rather
 * than resolving its own is what stops that, and it is why this sits beside `formatEur` instead of in
 * `@gladis/ui`: a second derivation of "which locale" is the defect 021 found in `format.ts`.
 *
 * TRAILING ZEROES ARE DROPPED. A split is entered in whole percents far more often than not, and a
 * column reading `50,00%` and `50,00%` spends four characters a row saying nothing.
 *
 * @param percentage - a percentage, not a fraction. `60` is sixty percent.
 */
export function formatPercent(percentage: number): string {
  // `Intl`'s percent style multiplies by 100, so the fraction is what it wants.
  return percentFormatterFor(activeLocale).format(percentage / 100);
}

/**
 * Formats an amount as EUR with exactly two decimal places, in the reader's own number format.
 *
 * @param amount - a number of euros, not cents. `12.5` is twelve euros fifty.
 */
export function formatEur(amount: number): string {
  const format = formatterFor(activeLocale);

  /*
   * `Object.is` rather than `===`, so negative zero is caught. A subtraction that lands exactly on
   * zero can produce -0, and `Intl` faithfully renders it as "-€0,00" — which reads as a debt of
   * nothing and looks like a bug to anyone who sees it.
   */
  if (Object.is(amount, -0)) return format.format(0);

  return format.format(amount);
}
